import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../components/UI/PageTransition';
import {
  FaCrown, FaHeart, FaLightbulb, FaShoppingBag,
  FaStar, FaBolt, FaCheck, FaCoins, FaFire, FaRocket,
  FaClock, FaHistory, FaCircleNotch, FaTimes
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Button from '../components/UI/Button';
import CoinDisplay from '../components/UI/CoinDisplay';
import PaymentModal from '../components/UI/PaymentModal';
import CoinPaymentModal from '../components/UI/CoinPaymentModal';
import Avatar from '../components/UI/Avatar';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { FRAME_CATALOG, FRAME_KEYFRAMES, TIER_LABELS } from '../utils/frameCatalog';

const PREMIUM_FEATURES = [
  'Vies illimitées en mode Survie',
  'Thèmes de quiz exclusifs',
  'Tournois et championnats réservés Premium',
  'Badge couronne sur ton profil',
  'Parrainage rémunéré',
];

const COIN_PACKS = [
  {
    id: 'starter',
    label: 'Pack Starter',
    coins: 500,
    price: 500,
    amountLabel: '500 FCFA',
    icon: FaCoins,
    color: '#94a3b8',
    badge: null,
  },
  {
    id: 'standard',
    label: 'Pack Standard',
    coins: 1500,
    price: 1000,
    amountLabel: '1 000 FCFA',
    icon: FaFire,
    color: '#f0b429',
    badge: 'POPULAIRE',
  },
  {
    id: 'pro',
    label: 'Pack Pro',
    coins: 4000,
    price: 2500,
    amountLabel: '2 500 FCFA',
    icon: FaRocket,
    color: '#8b5cf6',
    badge: null,
  },
  {
    id: 'mega',
    label: 'Pack Méga',
    coins: 10000,
    price: 5000,
    amountLabel: '5 000 FCFA',
    icon: FaCrown,
    color: '#ef4444',
    badge: 'MEILLEURE VALEUR',
  },
];

const CONSUMABLES = [
  {
    id: 'lives_3',
    label: '+3 Vies',
    description: 'Récupère 3 vies pour continuer tes parties',
    icon: FaHeart,
    color: '#ef4444',
    price: 100,
    priceLabel: '100 pts',
  },
  {
    id: 'hints_5',
    label: 'x5 Indices',
    description: 'Élimine 2 mauvaises réponses',
    icon: FaLightbulb,
    color: '#f0b429',
    price: 150,
    priceLabel: '150 pts',
  },
  {
    id: 'double_xp',
    label: 'XP x2 (1h)',
    description: 'Double tes points pendant 1 heure',
    icon: FaBolt,
    color: '#8b5cf6',
    price: 200,
    priceLabel: '200 pts',
  },
];

function Shop() {
  const { user, updateUser } = useAuth();
  const [purchasing, setPurchasing]             = useState(null);
  const [paymentOpen, setPaymentOpen]           = useState(false);
  const [selectedPlan, setSelectedPlan]         = useState('monthly');
  const [coinModalOpen, setCoinModalOpen]       = useState(false);
  const [selectedCoinPack, setSelectedCoinPack] = useState(null);
  const [myRequests, setMyRequests]             = useState([]);
  const [requestsLoading, setRequestsLoading]   = useState(false);
  const [owned, setOwned]                       = useState([]);
  const [equipped, setEquipped]                 = useState(null);
  const [frameAction, setFrameAction]           = useState(null);

  useEffect(() => {
    setRequestsLoading(true);
    api.get('/shop/coin-requests')
      .then(({ data }) => setMyRequests(data.requests || []))
      .catch(() => {})
      .finally(() => setRequestsLoading(false));
  }, [coinModalOpen]); // recharge après fermeture du modal d'achat

  useEffect(() => {
    api.get('/frames')
      .then(({ data }) => { setOwned(data.owned || []); setEquipped(data.equipped || null); })
      .catch(() => {});
  }, []);

  const handleBuyFrame = async (frameId, price) => {
    if ((user?.coins || 0) < price) {
      toast.error('Pas assez de pièces !');
      return;
    }
    setFrameAction(frameId);
    try {
      const { data } = await api.post(`/frames/${frameId}/buy`);
      setOwned((prev) => [...prev, frameId]);
      setEquipped(data.equipped);
      updateUser({ ...user, coins: data.coins, equipped_frame_id: data.equipped });
      toast.success('Cadre acheté et équipé ! ✅');
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de l'achat");
    } finally {
      setFrameAction(null);
    }
  };

  const handleEquipFrame = async (frameId) => {
    setFrameAction(frameId);
    try {
      const { data } = await api.post(`/frames/${frameId}/equip`);
      setEquipped(data.equipped);
      updateUser({ ...user, equipped_frame_id: data.equipped });
      toast.success(data.equipped ? 'Cadre équipé !' : 'Cadre retiré');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setFrameAction(null);
    }
  };

  const isPremium = user?.plan === 'premium' || user?.role === 'admin';
  const lives        = user?.lives        ?? 0;
  const hints        = user?.hints        ?? 0;
  const doubleXpEnd  = user?.double_xp_until ? new Date(user.double_xp_until) : null;
  const doubleXpActive = doubleXpEnd && doubleXpEnd > new Date();

  const handleBuyPremium = (plan = 'monthly') => {
    setSelectedPlan(plan);
    setPaymentOpen(true);
  };

  const handleBuyCoinPack = (pack) => {
    setSelectedCoinPack(pack);
    setCoinModalOpen(true);
  };

  const handleBuyConsumable = async (item) => {
    if ((user?.coins || 0) < item.price) {
      toast.error('Pas assez de pièces !');
      return;
    }
    setPurchasing(item.id);
    try {
      const { data } = await api.post('/shop/buy', { itemId: item.id });
      // Mise à jour avec les données du serveur
      if (data.user) {
        updateUser({ ...user, ...data.user });
        toast.success(`${item.label} acheté! ✅`);
      } else {
        console.error('Pas de user dans la réponse:', data);
        toast.error('Erreur de mise à jour');
      }
      toast.success(`${item.label} acheté !`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <PageTransition variant="slideIn">
    <PaymentModal open={paymentOpen} onClose={() => setPaymentOpen(false)} plan={selectedPlan} />
    <CoinPaymentModal
      open={coinModalOpen}
      onClose={() => { setCoinModalOpen(false); setSelectedCoinPack(null); }}
      pack={selectedCoinPack}
    />
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
      {/* Navbar */}
      <div className="flex items-center justify-between px-4 pt-6 pb-4">
        <h1
          className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          <FaShoppingBag className="inline mr-2 mb-1" />
          BOUTIQUE
        </h1>
        <CoinDisplay coins={user?.coins || 0} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4">

        {/* Section Premium */}
        {isPremium ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div
              className="rounded-3xl overflow-hidden border border-yellow-500/40 p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(240,180,41,0.15) 0%, rgba(26,74,46,0.8) 100%)',
                boxShadow: '0 0 40px rgba(240,180,41,0.15)',
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.4)' }}
                >
                  <FaCrown size={28} className="text-qfl-gold" style={{ filter: 'drop-shadow(0 0 8px rgba(240,180,41,0.6))' }} />
                </div>
                <div className="flex-1">
                  <h2
                    className="text-xl font-black uppercase tracking-widest text-qfl-gold"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    Compte Premium actif
                  </h2>
                  <p className="text-white/60 text-xs mt-0.5">Tu bénéficies de tous les avantages Premium</p>
                </div>
                <div
                  className="px-3 py-1.5 rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)' }}
                >
                  <FaCheck size={14} className="text-green-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {PREMIUM_FEATURES.map((feat, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <FaCheck size={10} className="text-qfl-gold flex-shrink-0" />
                    <span className="text-white/60 text-xs">{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div
                className="rounded-3xl overflow-hidden border border-yellow-500/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(240,180,41,0.15) 0%, rgba(26,74,46,0.8) 100%)',
                  boxShadow: '0 0 40px rgba(240,180,41,0.1)',
                }}
              >
                <div
                  className="p-4 text-center"
                  style={{ background: 'linear-gradient(135deg, rgba(240,180,41,0.2), transparent)' }}
                >
                  <FaCrown size={36} className="text-qfl-gold mx-auto mb-2" style={{ filter: 'drop-shadow(0 0 10px rgba(240,180,41,0.5))' }} />
                  <h2
                    className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    PREMIUM
                  </h2>
                  <p className="text-white/60 text-sm mt-1">Passe au niveau supérieur</p>
                </div>
                <div className="px-5 py-4">
                  {PREMIUM_FEATURES.map((feat, i) => (
                    <div key={i} className="flex items-center gap-3 py-2">
                      <FaCheck size={12} className="text-qfl-gold flex-shrink-0" />
                      <span className="text-white/80 text-sm">{feat}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 pb-5">
                  <div className="text-center mb-3">
                    <span
                      className="text-3xl font-black text-qfl-gold"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}
                    >
                      1 000 FCFA
                    </span>
                    <span className="text-white/40 text-sm">/mois</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button variant="gold" size="lg" fullWidth onClick={() => handleBuyPremium('monthly')} className="animate-pulse-gold">
                      <FaCrown size={18} />
                      MENSUEL — 1 000 FCFA/mois
                    </Button>
                    <Button variant="secondary" size="md" fullWidth onClick={() => handleBuyPremium('yearly')}>
                      ANNUEL — 10 000 FCFA/an <span className="ml-1 text-xs opacity-70">(économisez 17%)</span>
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>

            <div
              className="rounded-xl p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"
              style={{
                background: 'linear-gradient(135deg, rgba(240,180,41,0.10) 0%, rgba(240,180,41,0.03) 100%)',
                border: '1px solid rgba(240,180,41,0.22)',
              }}
            >
              <div className="flex items-center gap-2">
                <FaCrown size={14} className="text-qfl-gold flex-shrink-0" />
                <span className="text-white/70 text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  Premium inclus :
                </span>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-white/50 text-xs flex items-center gap-1"><FaHeart size={10} className="text-red-400" /> Vies illimitées</span>
                <span className="text-white/50 text-xs flex items-center gap-1"><FaCheck size={10} className="text-qfl-gold" /> Tournois</span>
                <span className="text-white/50 text-xs flex items-center gap-1"><FaCrown size={10} className="text-qfl-gold" /> Badge couronne</span>
                <span className="text-white/50 text-xs flex items-center gap-1"><FaCheck size={10} className="text-qfl-gold" /> Parrainage</span>
              </div>
            </div>
          </>
        )}

        {/* Inventaire actuel */}
        <div className="mb-5">
          <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            MON INVENTAIRE
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="card text-center py-3">
              <FaHeart size={20} className="text-red-400 mx-auto mb-1" />
              <p className="font-black text-xl text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {isPremium ? '∞' : lives}
              </p>
              <p className="text-white/40 text-xs">Vies</p>
            </div>
            <div className="card text-center py-3">
              <FaLightbulb size={20} className="text-yellow-400 mx-auto mb-1" />
              <p className="font-black text-xl text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{hints}</p>
              <p className="text-white/40 text-xs">Indices</p>
            </div>
            <div className="card text-center py-3">
              <FaBolt size={20} className="mx-auto mb-1" style={{ color: doubleXpActive ? '#8b5cf6' : 'rgba(255,255,255,0.2)' }} />
              <p className="font-black text-sm text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {doubleXpActive ? 'ACTIF' : 'OFF'}
              </p>
              <p className="text-white/40 text-xs">XP x2</p>
            </div>
          </div>
        </div>

        {/* Section Packs de Pièces */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-qfl-gold font-bold uppercase tracking-widest text-sm"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              <FaCoins className="inline mr-2 mb-0.5" size={13} />
              PACKS DE PIÈCES
            </h3>
            <span className="text-white/30 text-xs">Paiement Mobile Money</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {COIN_PACKS.map((pack, i) => (
              <motion.button
                key={pack.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                onClick={() => handleBuyCoinPack(pack)}
                className="relative rounded-2xl p-4 text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${pack.color}15 0%, rgba(13,43,26,0.9) 100%)`,
                  border: `1px solid ${pack.color}35`,
                }}
              >
                {pack.badge && (
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider whitespace-nowrap"
                    style={{
                      background: pack.color,
                      color: pack.id === 'standard' ? '#0d2b1a' : 'white',
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  >
                    {pack.badge}
                  </div>
                )}
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center mb-2"
                  style={{ background: `${pack.color}20`, border: `1px solid ${pack.color}40` }}
                >
                  <pack.icon size={17} style={{ color: pack.color }} />
                </div>
                <div
                  className="font-black text-white text-base leading-none"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}
                >
                  {pack.coins.toLocaleString()}
                </div>
                <div className="text-white/40 text-xs mb-3">pièces</div>
                <div
                  className="font-black text-sm"
                  style={{ color: pack.color, fontFamily: "'Rajdhani', sans-serif" }}
                >
                  {pack.amountLabel}
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Section Consommables */}
        <div>
          <h3
            className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            CONSOMMABLES
          </h3>

          <div className="flex flex-col gap-3">
            {CONSUMABLES.filter((item) => !(isPremium && item.id === 'lives_3')).map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 card"
              >
                {/* Icône */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${item.color}20`, border: `1px solid ${item.color}40` }}
                >
                  <item.icon size={22} style={{ color: item.color }} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div
                    className="font-bold text-white text-base"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    {item.label}
                  </div>
                  <div className="text-white/50 text-xs">{item.description}</div>
                </div>

                {/* Prix + bouton */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span
                    className="text-qfl-gold font-black text-base"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    {item.priceLabel}
                  </span>
                  <Button
                    variant="gold"
                    size="sm"
                    loading={purchasing === item.id}
                    onClick={() => handleBuyConsumable(item)}
                    className="text-xs py-1.5 px-3"
                  >
                    Acheter
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Section Cadres d'avatar (cosmétique, puits à pièces) */}
        <div className="mt-6">
          <style>{FRAME_KEYFRAMES}</style>
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-qfl-gold font-bold uppercase tracking-widest text-sm"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              CADRES D'AVATAR
            </h3>
            <span className="text-white/30 text-xs">Cosmétique</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {equipped && (
              <button
                onClick={() => handleEquipFrame('none')}
                disabled={frameAction === 'none'}
                className="rounded-2xl p-3 flex items-center gap-3 border border-white/10 bg-white/5 text-left"
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center bg-white/10 flex-shrink-0">
                  {frameAction === 'none' ? <FaCircleNotch className="animate-spin text-white/50" size={14} /> : <FaTimes className="text-white/50" size={14} />}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Aucun cadre</div>
                  <div className="text-white/40 text-xs">Revenir au cadre de niveau</div>
                </div>
              </button>
            )}
            {Object.entries(FRAME_CATALOG).map(([frameId, frame]) => {
              const isOwned    = owned.includes(frameId);
              const isEquipped = equipped === frameId;
              const canAfford  = (user?.coins || 0) >= frame.price;
              const tier = TIER_LABELS[frame.tier];
              return (
                <div
                  key={frameId}
                  className="rounded-2xl p-3 flex flex-col gap-2"
                  style={{
                    background: isEquipped ? 'rgba(240,180,41,0.08)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isEquipped ? 'rgba(240,180,41,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0" style={{ width: 48, height: 48, borderRadius: '50%', ...frame.style }}>
                      <div className="w-full h-full rounded-full flex items-center justify-center bg-qfl-dark text-white/30">
                        <FaCrown size={16} />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{frame.label}</div>
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: tier.color }}>{tier.label}</span>
                    </div>
                  </div>
                  {isOwned ? (
                    <Button
                      variant={isEquipped ? 'secondary' : 'gold'}
                      size="sm"
                      disabled={isEquipped}
                      loading={frameAction === frameId}
                      onClick={() => handleEquipFrame(frameId)}
                      className="text-xs py-1.5"
                    >
                      {isEquipped ? 'Équipé ✓' : 'Équiper'}
                    </Button>
                  ) : (
                    <Button
                      variant="gold"
                      size="sm"
                      disabled={!canAfford}
                      loading={frameAction === frameId}
                      onClick={() => handleBuyFrame(frameId, frame.price)}
                      className="text-xs py-1.5"
                    >
                      <FaCoins size={11} /> {frame.price.toLocaleString('fr-FR')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Section Mes achats de pièces */}
        {(myRequests.length > 0 || requestsLoading) && (
          <div className="mt-6">
            <h3
              className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              <FaHistory className="inline mr-2 mb-0.5" size={12} />
              MES ACHATS EN COURS
            </h3>

            {requestsLoading ? (
              <div className="flex justify-center py-4">
                <div className="w-5 h-5 rounded-full border-2 border-qfl-gold border-t-transparent animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {myRequests.map((r) => {
                  const STATUS = {
                    pending:  { label: 'En attente de validation', color: 'text-yellow-400', bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.25)'  },
                    approved: { label: 'Approuvé — pièces créditées', color: 'text-green-400', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.25)' },
                    rejected: { label: 'Non validé — contacter le support', color: 'text-red-400', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)' },
                  };
                  const s = STATUS[r.status] || STATUS.pending;
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 rounded-xl px-4 py-3"
                      style={{ background: s.bg, border: `1px solid ${s.border}` }}
                    >
                      <FaCoins size={16} className="text-qfl-gold flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-white font-bold text-sm"
                          style={{ fontFamily: "'Rajdhani', sans-serif" }}
                        >
                          +{Number(r.coins_amount).toLocaleString()} pièces
                          <span className="text-white/40 font-normal ml-1 text-xs">— {r.pack_label}</span>
                        </div>
                        <div className={`text-xs mt-0.5 flex items-center gap-1 ${s.color}`}>
                          <FaClock size={9} />
                          {s.label}
                        </div>
                      </div>
                      <span className="text-white/40 text-xs flex-shrink-0">
                        {Number(r.amount_fcfa).toLocaleString()} FCFA
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 2ème espace pub */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-6 flex items-center justify-center h-16">
          <span className="text-white/20 text-xs uppercase tracking-widest">
            Publicité
          </span>
        </div>

      </div>
    </div>
    </PageTransition>
  );
}

export default Shop;
